/**
 * Statistiche del sito con Umami (self-hosted, senza cookie).
 *
 * Il tracker è caricato da /api/uma/script.js: cil-server lo inoltra all'host
 * Umami e ripulisce di nuovo ogni evento. Qui, prima che qualcosa esca dal
 * browser:
 * - dall'URL resta solo path + utm_*; il referrer si riduce all'origine
 * - il ritorno da Stripe/PayPal non conta come nuova provenienza
 * - i dispositivi dello staff (admin/helper) non vengono contati
 * - il session replay parte solo con il consenso CookieYes "analytics"
 *
 * Gli eventi custom e le loro proprietà sono quelli di trackingPlan.json
 * (docs/STATISTICHE_PIANO_EVENTI.md): usare `track.<nomeEvento>(dati)`.
 * Con localStorage `cil.umami.debug` = "1" ogni evento è stampato in console.
 *
 * Senza REACT_APP_UMAMI_WEBSITE_ID non viene caricato nulla.
 */
import { TRACKED_EVENTS, validateEvent } from './trackingPlan';

const UUID_PATTERN =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
// Un id non valido (es. "$REACT_APP_UMAMI_WEBSITE_ID" non espanso dalla CI) spegne il tracker
const WEBSITE_ID = UUID_PATTERN.test(
  process.env.REACT_APP_UMAMI_WEBSITE_ID || ''
)
  ? process.env.REACT_APP_UMAMI_WEBSITE_ID
  : '';
const DOMAINS = process.env.REACT_APP_UMAMI_DOMAINS || 'www.cilassociazione.it';
const REPLAY_SAMPLE_RATE =
  process.env.REACT_APP_UMAMI_REPLAY_SAMPLE_RATE || '1';
const SCRIPT_BASE = '/api/uma';
const BEFORE_SEND = 'cilUmamiBeforeSend';
const STAFF_FLAG = 'umami.disabled';
const DEBUG_FLAG = 'cil.umami.debug';
const ATTRIBUTION_KEY = 'cil.attribution';
const ATTRIBUTION_MAX_AGE_MS = 30 * 24 * 60 * 60 * 1000;

export { TRACKED_EVENTS };

const UTM_PARAMS = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_content',
  'utm_term',
];
const UTM_VALUE_PATTERN = /^[a-z0-9_.-]{1,80}$/;
const PAYMENT_RETURN_HOSTS = [
  'stripe.com',
  'stripe.network',
  'paypal.com',
  'satispay.com',
];
const PAYMENT_RETURN_PATHS = ['/payment-result'];

const isPaymentReturnHost = (hostname) =>
  PAYMENT_RETURN_HOSTS.some(
    (host) => hostname === host || hostname.endsWith(`.${host}`)
  );

const readUtm = (searchParams) => {
  const utm = {};
  UTM_PARAMS.forEach((key) => {
    const value = (searchParams.get(key) || '').trim().toLowerCase();
    if (UTM_VALUE_PATTERN.test(value)) utm[key] = value;
  });
  return utm;
};

/** Origine + path + soli utm_* validi. */
export const cleanUrl = (raw) => {
  try {
    const url = new URL(raw, window.location.href);
    const params = new URLSearchParams(readUtm(url.searchParams));
    const query = params.toString();
    return `${url.origin}${url.pathname}${query ? `?${query}` : ''}`;
  } catch {
    return '';
  }
};

/** Solo origine; vuoto per pagamenti, stesso sito o valori non web. */
export const cleanReferrer = (raw) => {
  if (!raw) return '';
  try {
    const url = new URL(raw);
    const hostname = url.hostname.toLowerCase();
    if (url.protocol !== 'https:' && url.protocol !== 'http:') return '';
    if (isPaymentReturnHost(hostname)) return '';
    if (hostname === window.location.hostname) return '';
    return `${url.origin}/`;
  } catch {
    return '';
  }
};

export const umamiBeforeSend = (type, payload) => {
  if (type !== 'event' || !payload) return null;

  // Eventi custom (anche dai click con data-umami-event): solo dal piano eventi
  let eventData;
  if (payload.name) {
    const { ok, data } = validateEvent(payload.name, payload.data);
    if (!ok) return null;
    eventData = Object.keys(data).length ? data : undefined;
  }

  const url = cleanUrl(payload.url);
  if (!url) return null;
  const path = new URL(url).pathname;
  const referrer = PAYMENT_RETURN_PATHS.includes(path)
    ? ''
    : cleanReferrer(payload.referrer);

  // Niente id persistenti né tag: solo i campi che servono alle statistiche
  const { id, tag, data, ...rest } = payload;
  return {
    ...rest,
    url,
    referrer,
    ...(eventData && { data: eventData }),
  };
};

const storage = (kind) => {
  try {
    return window[kind];
  } catch {
    return null;
  }
};

const isStaff = (user) => user?.type === 'admin' || user?.type === 'helper';

/** Una volta usato da staff, il dispositivo resta escluso dalle statistiche. */
export const syncStaffExclusion = (user) => {
  if (!isStaff(user)) return;
  try {
    storage('localStorage')?.setItem(STAFF_FLAG, '1');
  } catch {
    // storage non disponibile: il dispositivo verrà contato
  }
};

const addScript = (src, attributes) => {
  const node = document.createElement('script');
  node.defer = true;
  node.src = src;
  Object.entries(attributes).forEach(([key, value]) =>
    node.setAttribute(key, value)
  );
  document.head.appendChild(node);
  return node;
};

let loaded = false;
let ready = false;
let replayRequested = false;
let replayStarted = false;
const pending = [];

const hasAnalyticsConsent = () => {
  try {
    return Boolean(window.getCkyConsent?.()?.categories?.analytics);
  } catch {
    return false;
  }
};

/**
 * Oltre la visita corrente l'attribuzione si conserva solo con il consenso
 * "analytics": senza, resta in sessionStorage e sparisce chiudendo la scheda.
 */
const persistAttribution = () => {
  if (!hasAnalyticsConsent()) return;
  try {
    const raw = storage('sessionStorage')?.getItem(ATTRIBUTION_KEY);
    if (raw) storage('localStorage')?.setItem(ATTRIBUTION_KEY, raw);
  } catch {
    // ignorato
  }
};

/**
 * Attribuzione per gli ordini: ultima provenienza non diretta della visita
 * (utm o sito esterno), scade dopo 30 giorni. Nessun dato personale.
 */
export const captureAttribution = () => {
  const session = storage('sessionStorage');
  if (!session) return;
  try {
    const { pathname, search } = window.location;
    if (PAYMENT_RETURN_PATHS.includes(pathname)) return;
    const utm = readUtm(new URLSearchParams(search));
    const referrer = cleanReferrer(document.referrer);
    const referrerHost = referrer ? new URL(referrer).hostname : '';
    if (!Object.keys(utm).length && !referrerHost) return;

    const attribution = {
      ...utm,
      ...(referrerHost && { referrer_host: referrerHost }),
      landing_path: pathname.slice(0, 200),
      captured_at: new Date().toISOString(),
    };
    session.setItem(ATTRIBUTION_KEY, JSON.stringify(attribution));
    persistAttribution();
  } catch {
    // attribuzione non disponibile
  }
};

/** Da allegare alle richieste di prenotazione/tesseramento. */
export const getAttribution = () => {
  try {
    const raw =
      storage('sessionStorage')?.getItem(ATTRIBUTION_KEY) ||
      storage('localStorage')?.getItem(ATTRIBUTION_KEY);
    if (!raw) return null;
    const attribution = JSON.parse(raw);
    const age = Date.now() - new Date(attribution.captured_at).getTime();
    return age >= 0 && age <= ATTRIBUTION_MAX_AGE_MS ? attribution : null;
  } catch {
    return null;
  }
};

// Richieste che creano un ordine o un pagamento: ordine socio (/api/ordini),
// ospite (/api/public/eventi/:slug/ordini) e pagamento Stripe
// (/api/create-payment-intent: il webhook copia la provenienza sull'ordine che
// crea, anche per la tessera pagata con carta)
const ORDER_REQUEST =
  /^\/api\/(ordini|public\/eventi\/[^/]+\/ordini|create-payment-intent)\/?$/;

/**
 * Allega l'attribuzione (header X-CIL-Attribution) alle richieste che creano
 * un ordine o un pagamento, così cil-server la salva in order.attribution.
 */
export const installAttributionHeader = (axiosInstance) =>
  axiosInstance.interceptors.request.use((config) => {
    const method = (config.method || 'get').toLowerCase();
    if (method !== 'post' || !ORDER_REQUEST.test(config.url || '')) {
      return config;
    }
    const attribution = getAttribution();
    if (!attribution) return config;
    try {
      config.headers.set(
        'X-CIL-Attribution',
        window.btoa(JSON.stringify(attribution))
      );
    } catch {
      // attribuzione non codificabile: l'ordine parte comunque
    }
    return config;
  });

const startReplay = () => {
  replayRequested = true;
  if (replayStarted || !ready) return;
  replayStarted = true;
  addScript(`${SCRIPT_BASE}/recorder.js`, {
    'data-website-id': WEBSITE_ID,
    'data-sample-rate': REPLAY_SAMPLE_RATE,
    // strict: anche i testi della pagina sono mascherati (nomi, email, ordini)
    'data-mask-level': 'strict',
    'data-max-duration': '300000',
  });
};

const watchConsent = () => {
  const onConsent = (event) => {
    const accepted = event?.detail?.accepted || [];
    if (accepted.indexOf('analytics') !== -1 || hasAnalyticsConsent()) {
      startReplay();
      persistAttribution();
    } else if ((event?.detail?.rejected || []).indexOf('analytics') !== -1) {
      try {
        storage('localStorage')?.removeItem(ATTRIBUTION_KEY);
      } catch {
        // ignorato
      }
    }
  };
  document.addEventListener('cookieyes_consent_update', onConsent);
  document.addEventListener('cookieyes_banner_load', onConsent);
  if (hasAnalyticsConsent()) startReplay();
};

/** Carica il tracker. Da chiamare una volta, dopo aver letto l'utente. */
export const initUmami = (user) => {
  syncStaffExclusion(user);
  if (loaded || !WEBSITE_ID || typeof document === 'undefined') return;
  loaded = true;
  captureAttribution();

  window[BEFORE_SEND] = umamiBeforeSend;
  const script = addScript(`${SCRIPT_BASE}/script.js`, {
    'data-website-id': WEBSITE_ID,
    'data-host-url': SCRIPT_BASE,
    'data-domains': DOMAINS,
    'data-before-send': BEFORE_SEND,
  });
  script.addEventListener('load', () => {
    ready = Boolean(window.umami);
    pending
      .splice(0)
      .forEach(([name, data]) => window.umami?.track(name, data));
    if (replayRequested) startReplay();
  });
  watchConsent();
};

const isDebug = () => {
  try {
    return storage('localStorage')?.getItem(DEBUG_FLAG) === '1';
  } catch {
    return false;
  }
};

const report = (name, result) => {
  if (isDebug()) {
    // eslint-disable-next-line no-console
    console.info(
      `[umami] ${result.ok ? 'invio' : 'SCARTATO'} ${name}`,
      result.data,
      result.problems.length ? result.problems : ''
    );
  } else if (process.env.NODE_ENV === 'development' && result.problems.length) {
    // eslint-disable-next-line no-console
    console.warn(`[umami] ${name}: ${result.problems.join('; ')}`);
  }
};

/**
 * Evento custom del piano eventi: proprietà non previste o non valide vengono
 * tolte, senza le obbligatorie l'evento non parte. Mai dati personali.
 */
// Eventi da contare una volta sola finché la pagina resta aperta: un nuovo
// tentativo di pagamento o un doppio clic non sono un nuovo checkout
const ONCE_KEYS = {
  checkout_start: (d) => `${d.event_id}|${d.metodo}`,
};
const sentOnce = new Set();

export const trackEvent = (name, data) => {
  const result = validateEvent(name, data);
  report(name, result);
  if (!result.ok || !WEBSITE_ID) return;
  if (ONCE_KEYS[name]) {
    const key = `${name}|${ONCE_KEYS[name](result.data)}`;
    if (sentOnce.has(key)) return;
    sentOnce.add(key);
  }
  const payload = Object.keys(result.data).length ? result.data : undefined;
  if (ready && window.umami) {
    window.umami.track(name, payload);
  } else if (pending.length < 50) {
    pending.push([name, payload]);
  }
};

const toCamelCase = (name) =>
  name.replace(/_([a-z])/g, (_, letter) => letter.toUpperCase());

/** Una funzione per evento del piano: track.bookingComplete({...}) */
export const track = Object.fromEntries(
  TRACKED_EVENTS.map((name) => [
    toCamelCase(name),
    (data) => trackEvent(name, data),
  ])
);

/**
 * Metodo ed esito di un ordine, nel formato del piano eventi.
 * paymentMethod/paymentStatus sono quelli inviati a /api/ordini.
 * Nessun incasso: il totale ufficiale è in Strapi e in Umami ne mancherebbe
 * una parte (adblock, ordini dell'admin), con due numeri diversi.
 */
export const orderOutcome = ({ paymentMethod, paymentStatus } = {}) => {
  const paid = paymentStatus === 'succeeded';
  if (paymentMethod === 'elettronico')
    return { metodo: 'stripe', esito: paid ? 'confermata' : 'in_attesa' };
  if (paymentMethod === 'punti')
    return { metodo: 'punti', esito: 'confermata' };
  // Dal sito un ordine "contanti" già pagato è un ordine gratuito
  if (paid) return { metodo: 'gratuito', esito: 'confermata' };
  return { metodo: 'contanti', esito: 'in_attesa' };
};
